import { useComputed } from "@suse-o11y/signal";
import { useContext, useLayoutEffect } from "react";

import { usePermanent } from "~/tools/use-permanent";

import type { Drawer } from "../_setup-dashboard-widget-drawer/drawer";

import { DashboardContext, DashboardWidgetContext } from "./_context";

/**
 * Hook to retrieve the state associated with a specific dashboard widget.
 *
 * @param init Function to initialize the state when it does not exist.
 *
 * @returns The state associated with the dashboard widget.
 */
export function useDashboardWidgetState<T>(init: () => T): T {
	/**
	 * Widgets can be used outside of a dashboard context,
	 * for instance in {@link Drawer} component,
	 * so the context can be null.
	 */
	const widgets = useContext(DashboardContext)?.widgets;
	const widgetIdentifier = useContext(DashboardWidgetContext);

	const stateFromContext = useComputed(
		(monitor) => {
			if (widgetIdentifier == null) {
				return undefined;
			}

			// We ensure that a state is created once per widgetIdentifier, so the unknown type here is safe to cast.
			return widgets?.read(monitor).get(widgetIdentifier) as undefined | T;
		},
		[widgets, widgetIdentifier],
	);

	/**
	 * When consumer mounts for the first time, it the {@link stateFromContext} is undefined,
	 * so it calls {@link init} to create the state.
	 * On subsequent remounts, the existing state is used.
	 */
	const state = usePermanent(() => stateFromContext ?? init());

	useLayoutEffect(() => {
		if (widgetIdentifier != null) {
			widgets?.write((current) => current.set(widgetIdentifier, state));
		}
	}, [widgets, widgetIdentifier, state]);

	return state;
}
